/**
 * Privacy Policy (/docs/privacy): the formal document submitted to stores and
 * publishers. /privacy is the plain-language summary; this page carries the
 * binding wording, and both must state the same facts
 * (docs/superpowers/specs/2026-10-25-privacy-page-redesign-design.md §5).
 *
 * Sections are numbered by a CSS counter (docs.scss, .privacy-page), not in
 * the catalogs, so translations never carry numbers that could drift. List
 * items are '|'-separated in one key, as elsewhere in the docs catalogs.
 */

import type { ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { POLICY_THIRD_PARTIES } from './privacyPolicySections';
import './docs.scss';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="privacy-page__section privacy-page__section--numbered">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Items({ value }: { value: string }) {
  return (
    <ul>
      {value.split('|').map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

export function PrivacyPolicy() {
  const { t } = useI18n();

  return (
    <div className="docs-content privacy-page">
      <h1>{t('privacy.title')}</h1>
      <p><em>{t('privacy.lastUpdated')}</em></p>

      <Section title={t('privacy.intro.title')}>
        <p>{t('privacy.intro.content')}</p>
      </Section>

      <div className="privacy-page__guarantee">
        <h3>
          <Shield size={20} />
          {t('privacy.guarantee.title')}
        </h3>
        <p>{t('privacy.guarantee.content')}</p>
        <Items value={t('privacy.guarantee.items')} />
      </div>

      <Section title={t('privacy.collect.title')}>
        <p>{t('privacy.collect.content')}</p>

        <h3>{t('privacy.account.title')}</h3>
        <p>{t('privacy.account.content')}</p>
        <Items value={t('privacy.account.items')} />

        <h3>{t('privacy.signin.title')}</h3>
        <p>{t('privacy.signin.content')}</p>

        <h3>{t('privacy.abuse.title')}</h3>
        <p>{t('privacy.abuse.content')}</p>

        <h3>{t('privacy.billing.title')}</h3>
        <p>{t('privacy.billing.content')}</p>

        <h3>{t('privacy.collect.analytics.title')}</h3>
        <div className="privacy-page__highlight">
          <p>{t('privacy.collect.analytics.content')}</p>
          <Items value={t('privacy.collect.analytics.items')} />
          <p>{t('privacy.collect.analytics.identity')}</p>
        </div>

        <h3>{t('privacy.feedback.title')}</h3>
        <p>{t('privacy.feedback.content')}</p>
      </Section>

      <Section title={t('privacy.localOnly.title')}>
        <p>{t('privacy.localOnly.content')}</p>
        <Items value={t('privacy.localOnly.items')} />
      </Section>

      <Section title={t('privacy.storage.transmission.title')}>
        <p>{t('privacy.storage.transmission.content')}</p>
      </Section>

      <Section title={t('privacy.use.title')}>
        <Items value={t('privacy.use.items')} />
      </Section>

      <Section title={t('privacy.thirdParty.title')}>
        <p>{t('privacy.thirdParty.content')}</p>
        {POLICY_THIRD_PARTIES.map((id) => (
          <div key={id}>
            <h3>{t(`privacy.thirdParty.${id}.title`)}</h3>
            <p>{t(`privacy.thirdParty.${id}.content`)}</p>
          </div>
        ))}
      </Section>

      <Section title={t('privacy.transfer.title')}>
        <p>{t('privacy.transfer.content')}</p>
      </Section>

      <Section title={t('privacy.storage.title')}>
        <p>{t('privacy.storage.content')}</p>
      </Section>

      <Section title={t('privacy.retention.title')}>
        <p>{t('privacy.retention.content')}</p>
        <Items value={t('privacy.retention.items')} />
      </Section>

      <Section title={t('privacy.deletion.title')}>
        <p>{t('privacy.deletion.content')}</p>
        <Items value={t('privacy.deletion.items')} />
      </Section>

      <Section title={t('privacy.rights.title')}>
        <p>{t('privacy.rights.content')}</p>
        <Items value={t('privacy.rights.items')} />
      </Section>

      <Section title={t('privacy.gdpr.title')}>
        <p>{t('privacy.gdpr.content')}</p>
      </Section>

      <Section title={t('privacy.children.title')}>
        <p>{t('privacy.children.content')}</p>
      </Section>

      <Section title={t('privacy.changes.title')}>
        <p>{t('privacy.changes.content')}</p>
      </Section>

      <Section title={t('privacy.contact.title')}>
        <p>{t('privacy.contact.content')}</p>
        <div className="privacy-page__contact">
          <p>{t('privacy.contact.email')}</p>
          <p>{t('privacy.contact.privacy')}</p>
          <p>{t('privacy.contact.github')}</p>
        </div>
      </Section>
    </div>
  );
}
